import { queryOptions, useQuery, type QueryClient } from '@tanstack/react-query'
import { getCurrentMember, getFileQuota, getMemberRegistration, getPointBalance, getPointTransactions } from '../api/member-api'
import { useMemberSession } from './member-session'

export const memberKeys = {
  all: ['member'] as const,
  me: () => [...memberKeys.all, 'me'] as const,
  registration: () => [...memberKeys.all, 'registration'] as const,
  points: () => [...memberKeys.all, 'points'] as const,
  fileQuota: () => [...memberKeys.all, 'file-quota'] as const,
  pointTransactionsAll: () => [...memberKeys.all, 'point-transactions'] as const,
  pointTransactions: (page: number, size: number) => [...memberKeys.all, 'point-transactions', page, size] as const,
}

export async function refreshMemberPoints(queryClient: QueryClient) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: memberKeys.points() }),
    queryClient.invalidateQueries({ queryKey: memberKeys.pointTransactionsAll() }),
  ])
}

export function memberQueryOptions() {
  return queryOptions({ queryKey: memberKeys.me(), queryFn: async () => (await getCurrentMember()).data, staleTime: 5 * 60_000 })
}

export function useCurrentMember() {
  const authenticated = useMemberSession((state) => state.authenticated)
  return useQuery({ ...memberQueryOptions(), enabled: authenticated })
}

export function useMemberRegistration() {
  return useQuery({
    queryKey: memberKeys.registration(),
    queryFn: async () => (await getMemberRegistration()).data,
    staleTime: 5 * 60_000,
  })
}

export function usePointBalance() {
  const authenticated = useMemberSession((state) => state.authenticated)
  return useQuery({ queryKey: memberKeys.points(), queryFn: async () => (await getPointBalance()).data, enabled: authenticated, staleTime: 30_000 })
}

export function usePointTransactions(page = 1, size = 20) {
  const authenticated = useMemberSession((state) => state.authenticated)
  return useQuery({ queryKey: memberKeys.pointTransactions(page, size), queryFn: async () => (await getPointTransactions(page, size)).data, enabled: authenticated })
}

export function useFileQuota() {
  const authenticated = useMemberSession((state) => state.authenticated)
  return useQuery({ queryKey: memberKeys.fileQuota(), queryFn: async () => (await getFileQuota()).data, enabled: authenticated, staleTime: 30_000 })
}
